import { SelectChangeEvent } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { Metrics } from "../../types/Metrics";
import { CallLead } from "../../types/CallLead";
import { route, getAxios } from "../../utils/endpoints";
import { User } from "../../model/User";
import { GridSortModel } from "@mui/x-data-grid";
import { fillColumnAccess } from "../../components/LeadGrid/ColumnAccess";

interface IDashboardTable {
  id: number;
  description: string;
  items: any[];
  quantity: number;
  sortOrder: number;
}

export const useHome = () => {
  const user = new User();

  const hideMetrics = user.HasForbiddenAccess("Can Read Metrics");
  const hideTables = user.HasForbiddenAccess("Can Read Dashboard Tables");
  const columnAccess = fillColumnAccess(user);

  const [dashboards, setDashboards] = useState<IDashboardTable[]>([]);
  const [selectedDashboard, setSelectedDashboard] = useState<IDashboardTable>();
  const [dashboardValue, setDashboardValue] = useState<number>(0);
  const [dealerId, setDealerId] = useState<string>("Select an account");
  const [metrics, setMetrics] = useState<Metrics[]>([]);
  const [callLeads, setCallLeads] = useState<CallLead[]>([]);

  /** 🔴 IMPORTANT: loading defaults depend on permissions */
  const [isMetricsLoading, setIsMetricsLoading] = useState<boolean>(
    !hideMetrics
  );
  const [isLeadsLoading, setIsLeadsLoading] = useState<boolean>(!hideTables);

  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<string>("");
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "leadDate", sort: "asc" },
  ]);

  /* =====================================================
   * HANDLERS
   * ===================================================== */

  const onPageChange = (newPage: number) => setPage(newPage);
  const onPageSizeChange = (newPageSize: number) => setPageSize(newPageSize);

  const onChangeOrder = (newSortModel: GridSortModel) => {
    const currentField =
      newSortModel[0].field === "caller" ? "LastName" : newSortModel[0].field;

    setSortModel(newSortModel);
    setOrderBy(`${currentField} ${newSortModel[0].sort}`);
  };

  const onChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;

    if (value === "Select an account") {
      user.removeUserAccount();
      setDealerId(value);
      return;
    }

    user.UserAccount = value;
    setDealerId(value);
  };

  /* =====================================================
   * FETCH METRICS
   * ===================================================== */

  const fetchMetrics = useCallback(async () => {
    setIsMetricsLoading(true);

    try {
      const response = await getAxios(
        route.apiDashboard.metrics,
        "GET",
        undefined,
        { userAccount: user.UserAccount }
      );

      if (Array.isArray(response.data)) {
        setMetrics(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsMetricsLoading(false);
    }
  }, [user.UserAccount]);

  /* =====================================================
   * FETCH TABLES
   * ===================================================== */

  const fetchDashboardTables = async () => {
    setIsLeadsLoading(true);

    try {
      const response = await getAxios("/dashboard-tables", "GET", undefined, {
        titlesOnly: false,
        pageSize,
      });

      const data: IDashboardTable[] = response.data;
      setDashboards(data);

      if (!data.length) {
        setSelectedDashboard(undefined);
        setDashboardValue(0);
        return;
      }

      data.forEach((dashboard) => {
        dashboard.items = dashboard.items.filter((i) => i.id !== 0);
        dashboard.quantity = dashboard.items.length;
      });

      setDashboardValue(data[0].id);
      setSelectedDashboard(data[0]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLeadsLoading(false);
    }
  };

  /* =====================================================
   * EFFECT
   * ===================================================== */

  useEffect(() => {
    if (!hideMetrics) fetchMetrics();
    else setIsMetricsLoading(false); // 🔴 REQUIRED

    if (!hideTables) fetchDashboardTables();
    else setIsLeadsLoading(false); // 🔴 REQUIRED
  }, [fetchMetrics, page, pageSize, orderBy]);

  return {
    dealerId,
    metrics,
    dashboards,
    selectedDashboard,
    dashboardValue,
    page,
    pageSize,
    sortModel,
    callLeads,
    isLeadsLoading,
    isMetricsLoading,
    hideMetrics,
    hideTables,
    columnAccess,
    onPageChange,
    onPageSizeChange,
    onChange,
    onChangeOrder,
    moveToDashboard: (value: number) =>
      setSelectedDashboard(dashboards.find((d) => d.id === value)),
  };
};
